/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import Link from '@material-ui/core/Link';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import Checkbox from '@material-ui/core/Checkbox';
import Typography from '@material-ui/core/Typography';
import { ValidationError } from 'yup';

import {
  email as emailSchema,
  acceptTermsAndConditions as acceptTermsAndConditionsSchema,
  EmailValidationErrors,
} from 'containers/shared/validationSchemas';
import type { StepComponentProps } from 'components/Stepper';
import { useUpdateValidation } from 'components/shared/hooks';

import useStyles from './CreateAccount.style';

type CreateAccountProps = StepComponentProps;

export type CreateAccountFormValues = {
  email: string;
  acceptTermsAndConditions: boolean;
};

export const isCreateAccountFormValues = (
  stepValues: unknown
): stepValues is CreateAccountFormValues =>
  !!(
    (stepValues as CreateAccountFormValues).email &&
    ((stepValues as CreateAccountFormValues).acceptTermsAndConditions ===
      true ||
      false)
  );

const defaultValues: CreateAccountFormValues = {
  email: '',
  acceptTermsAndConditions: false,
};

const CreateAccount: React.FC<CreateAccountProps> = (props) => {
  const {
    onNext,
    updateValidation,
    formId,
    pending,
    errors: apiErrors,
  } = props;
  const { header, subheader, subtext, form, acceptTermsAndConditionsCheckbox } =
    useStyles();

  const {
    handleSubmit,
    control,
    trigger,
    setError,
    getValues,
    formState: { errors, isValid, touchedFields },
  } = useForm<CreateAccountFormValues>({
    mode: 'onTouched',
    defaultValues,
  });

  const [invalidUsernames, setInvalidUsernames] = useState<string[]>([]);

  // TODO: pass this in as a prop?
  const onSubmit: SubmitHandler<CreateAccountFormValues> = (values) => {
    if (onNext) {
      onNext(values);
    }
  };

  useEffect(() => {
    if (apiErrors) {
      apiErrors.forEach((error) => {
        if (error.error === 'duplicate username') {
          const duplicateUsername = getValues('email');
          setInvalidUsernames((currentInvalidUsernames) => [
            ...currentInvalidUsernames,
            duplicateUsername,
          ]);
          setError('email', { type: 'manual', message: error.error });
        }
      });
    }
  }, [apiErrors, setError, getValues]);

  useUpdateValidation(updateValidation, isValid);

  return (
    <>
      <Typography variant="h2" className={header}>
        Create Account
      </Typography>
      <Typography variant="body2" className={subheader}>
        Already have an account?&nbsp;
        <Link
          to="/signin"
          variant="button"
          component={RouterLink}
          underline="always"
        >
          Sign in
        </Link>
      </Typography>
      <Typography variant="body2" className={subtext}>
        By registering for a BitTitan account, you will have access to all
        BitTitan products and services. The selected product will be set as your
        default application, which you can change at any time during sign-in.
      </Typography>
      <form
        id={formId}
        className={form}
        aria-label="form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <TextField
              variant="outlined"
              size="small"
              required
              fullWidth
              id="email"
              label="Email Address"
              type="email"
              autoComplete="email"
              autoFocus
              disabled={pending}
              error={!!errors.email}
              helperText={errors.email?.message}
              {...field}
            />
          )}
          rules={{
            validate: async (value) => {
              try {
                await emailSchema.validate(value);
                if (invalidUsernames.includes(value)) {
                  throw new ValidationError(EmailValidationErrors.ValidEmail);
                }
              } catch (err) {
                return (err as ValidationError).message;
              }
              return true;
            },
          }}
        />
        <FormControl
          required
          error={!!errors.acceptTermsAndConditions}
          component="fieldset"
          disabled={pending}
          className={acceptTermsAndConditionsCheckbox}
        >
          <FormGroup>
            <FormControlLabel
              control={
                <Controller
                  name="acceptTermsAndConditions"
                  control={control}
                  render={({ field: { onChange, ...field } }) => (
                    <Checkbox
                      required
                      color="primary"
                      onChange={async (event) => {
                        /* manually trigger validation so 'isValid' updates as soon as checkbox 
                          is clicked, otherwise 'Next' button doesn't become enabled until blur */
                        onChange(event);
                        await trigger('acceptTermsAndConditions');
                      }}
                      {...field}
                    />
                  )}
                  rules={{
                    validate: async (value) => {
                      try {
                        await acceptTermsAndConditionsSchema.validate(value);
                      } catch (err) {
                        return (err as ValidationError).message;
                      }
                      return true;
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" color="textPrimary">
                  By registering, you agree to BitTitan&apos;s&nbsp;
                  <Link
                    // TODO: move link to const file
                    href="https://www.bittitan.com/legal/migrationwiz-user-agreement/"
                    target="_blank"
                    variant="button"
                    component="a"
                    underline="always"
                  >
                    Terms & Conditions
                  </Link>
                </Typography>
              }
            />
          </FormGroup>
          {touchedFields.acceptTermsAndConditions &&
            errors.acceptTermsAndConditions && (
              <FormHelperText>
                {errors.acceptTermsAndConditions?.message}
              </FormHelperText>
            )}
        </FormControl>
      </form>
    </>
  );
};

export default CreateAccount;
